import { AfterViewInit, Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { ParamMap } from '@angular/router';
import { BlockActionType } from '@core/enums';
import { ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { ComponentDependencyJunctionService } from '@core/services/component-dependency-junction.service';
import { SubSink } from '@core/utils/subsink';
import { FormService } from '@modules/creator/components/special-block/forms/services/form.service';
import { FiltersService } from '@shared/services/filters/filters.service';
import { filter, map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-list-filters',
  templateUrl: './list-filters.component.html',
  styleUrls: ['./list-filters.component.scss'],
  providers: [FormService, ComponentDependencyJunctionService],
  standalone: false,
})
export class ListFiltersComponent implements OnInit, AfterViewInit, OnDestroy {
  showResetBlock = true;
  @Input() specialBlock: Block;
  private subSink = new SubSink();

  constructor(public formService: FormService,
              public filterService: FiltersService,
              public buttonHandlerService: ButtonHandlerService,
              @Optional() public junctionService: ComponentDependencyJunctionService) {
    junctionService?.initFilterService(filterService);
  }

  get filtersBlock(): Block | undefined {
    return this.specialBlock.children.find(block => block.settings.viewPart === ViewPart.FILTERS_CONTENT);
  }

  get resetBlock(): Block | undefined {
    return this.specialBlock.children.find(block => block.settings.viewPart === ViewPart.RESET_CONTENT);
  }


  ngOnInit(): void {
    // Initialize extended_search checkbox
    this.formService.addControl('extended_search', false);

    // clear subscription
    this.subSink.sink = this.buttonHandlerService.click$
      .pipe(
        filter(({actionType}) => actionType === BlockActionType.CLEAR),
        tap(() => this.formService.clear()),
        tap(() => this.filterService.goTo({})),
      ).subscribe();

    // search Subscription
    this.subSink.sink = this.buttonHandlerService.click$
      .pipe(
        filter(({actionType}) => actionType === BlockActionType.SUBMIT),
        filter(() => this.formService.formGroup.valid),
        tap(_ => {
          if (this.junctionService?.textSearchActive) {
            this.formService.resetValue('search');
          }
        }), // remove search text from form
        tap(_ => this.filterService.search(this.formService.formGroup.value)),
      ).subscribe();

    // search Subscription
    this.subSink.sink = this.formService.keyboardEvent
      .pipe(
        filter(({code}) => ['NumpadEnter', 'Enter'].includes(code)),
        tap(_ => this.filterService.search(this.formService.formGroup.value)),
      ).subscribe();

    // Handle search field changes - reset extended_search when search is cleared
    this.subSink.sink = this.formService.changed
      .pipe(
        filter(({formControlName}) => formControlName === 'search'),
        tap(({event}) => {
          // If search field is empty, reset extended_search checkbox
          if (!event || event === '') {
            this.formService.formGroup.patchValue({extended_search: false});
          }
        }),
      ).subscribe();

    // initialize filter settings
    const {settings: {isSingleStateFilters, orientation, showClearFiltersButton}} = this.specialBlock;
    this.filterService.initSettings({isSingleStateFilters, orientation, showClearFiltersButton});
  }

  ngAfterViewInit(): void {
    this.subSink.sink = this.filterService.filtersFrom$
      .subscribe((params) => this.paramsChanged(params));

    this.subSink.sink = this.filterService.filtersToChanged
      .pipe(map(values => {
        if (!values) {
          return {};
        }

        const notEmptyKeys = Object.keys(values).filter((key) => {
          if (Array.isArray(values[key])) {
            return values[key].length > 0;
          }
          return values[key] && values[key].toString().length > 0;
        });

        const notEmptyParams: any = {};
        notEmptyKeys.forEach(key => notEmptyParams[key] = values[key]);

        return notEmptyParams;
      })).subscribe();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  private paramsChanged(params: ParamMap) {
    const ignoredParams = ['availability', 'sorting'];
    this.showResetBlock = params.keys.filter(key => !ignoredParams.includes(key)).length > 0;
    for (const key of params.keys) {
      // update search form by values
      this.formService.formGroup.get(key)?.patchValue(params.get(key));
    }
  }
}
